import { useEffect } from 'react'

const VERSION_PATH = `${import.meta.env.BASE_URL}version.json`
const VERSION_RELOAD_PARAM = '__qsc_version'
const VERSION_RELOAD_KEY = 'bizbee-qsc:version-reload-at'
const VERSION_RELOAD_COOLDOWN_MS = 60_000
const VERSION_CHECK_INTERVAL_MS = 5 * 60_000

type RemoteVersion = {
  version?: string
  buildId?: string
  gitSha?: string
  builtAt?: string
}

let pendingCheck: Promise<boolean> | null = null

function readReloadCooldown() {
  try {
    return Number(window.sessionStorage.getItem(VERSION_RELOAD_KEY) ?? 0)
  } catch {
    return 0
  }
}

function rememberReloadAttempt() {
  try {
    window.sessionStorage.setItem(VERSION_RELOAD_KEY, String(Date.now()))
  } catch {
    // storage may be unavailable in hardened WebView/browser contexts.
  }
}

function replaceWithBuildVersion(buildId: string) {
  const lastAttempt = readReloadCooldown()
  if (Number.isFinite(lastAttempt) && Date.now() - lastAttempt < VERSION_RELOAD_COOLDOWN_MS) return false

  rememberReloadAttempt()

  const url = new URL(window.location.href)
  url.searchParams.set(VERSION_RELOAD_PARAM, buildId)
  window.location.replace(url.toString())
  return true
}

export async function checkForAppVersionUpdate() {
  if (import.meta.env.DEV) return false
  if (pendingCheck) return pendingCheck

  pendingCheck = fetch(`${VERSION_PATH}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })
    .then(async (response) => {
      if (!response.ok) return false
      const remote = (await response.json()) as RemoteVersion
      if (!remote.buildId || remote.buildId === __QSC_BUILD_ID__) return false
      return replaceWithBuildVersion(remote.buildId)
    })
    .catch(() => false)
    .finally(() => {
      pendingCheck = null
    })

  return pendingCheck
}

export function useAppVersionGuard() {
  useEffect(() => {
    if (import.meta.env.DEV) return undefined

    const checkWhenVisible = () => {
      if (document.visibilityState === 'hidden') return
      void checkForAppVersionUpdate()
    }

    checkWhenVisible()

    const interval = window.setInterval(checkWhenVisible, VERSION_CHECK_INTERVAL_MS)
    window.addEventListener('focus', checkWhenVisible)
    window.addEventListener('pageshow', checkWhenVisible)
    window.addEventListener('online', checkWhenVisible)
    document.addEventListener('visibilitychange', checkWhenVisible)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', checkWhenVisible)
      window.removeEventListener('pageshow', checkWhenVisible)
      window.removeEventListener('online', checkWhenVisible)
      document.removeEventListener('visibilitychange', checkWhenVisible)
    }
  }, [])
}
